/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type ContentImportSource from 'embercom/models/content-service/content-import-source';
import {
  ActorName,
  type ContentSegmentRule,
} from 'embercom/models/content-service/content-import-source';
import { FinAvailability } from 'embercom/lib/ai-content-library/constants';
import type IntlService from 'ember-intl/services/intl';

export interface SaveAdvancedSettingsOptions {
  contentImportSource: ContentImportSource;
  formData: Record<string, unknown>;
  shouldResync: boolean;
  intl: IntlService;
  notificationsService: {
    notifyConfirmation: (message: string) => void;
    notifyError: (message: string) => void;
  };
  onRefresh: () => Promise<void>;
}

/**
 * Saves advanced settings for a content import source
 * Handles form data update, save, optional resync, notifications, and refresh
 */
export async function saveAdvancedSettings(options: SaveAdvancedSettingsOptions): Promise<void> {
  let { contentImportSource, formData, shouldResync, intl, notificationsService, onRefresh } =
    options;

  try {
    updateSourceFromFormData(contentImportSource, formData);
    await contentImportSource.save();

    if (shouldResync) {
      await contentImportSource.triggerResync();
    } else {
      notificationsService.notifyConfirmation(
        intl.t('knowledge-hub.filterable-list.sources.actions.source-saved'),
      );
    }

    await onRefresh();
  } catch (error: unknown) {
    if (!shouldResync) {
      notificationsService.notifyError(
        intl.t('knowledge-hub.filterable-list.sources.actions.source-save-error'),
      );
    }
    throw error;
  }
}

/**
 * Updates a ContentImportSource from form data submitted from the advanced settings sheet
 */
export function updateSourceFromFormData(
  source: ContentImportSource,
  formData: Record<string, unknown>,
): void {
  updateUrlLists(source, formData);
  updateCssSelectors(source, formData);
  updateCrawlerSettings(source, formData);
  updateAudienceSegments(source, formData);
  updateContentAccessSettings(source, formData);
}

function updateUrlLists(source: ContentImportSource, formData: Record<string, unknown>): void {
  if (formData.additionalUrls !== undefined) {
    let urls = parseMultilineUrls(formData.additionalUrls as string);
    source.additionalUrls = urls.length > 0 ? urls : ([] as any);
  }

  if (formData.excludedUrls !== undefined) {
    let excludedUrls = (formData.excludedUrls as { value: string }[])
      .map((item) => item.value.trim())
      .filter((url) => url);
    source.excludeUrlGlobs = excludedUrls.length > 0 ? excludedUrls : ([] as any);
  }
}

function updateCssSelectors(source: ContentImportSource, formData: Record<string, unknown>): void {
  if (formData.removeCssSelectors !== undefined) {
    let selectors = parseCommaSeparated(formData.removeCssSelectors as string);
    source.removeCssSelectors = selectors.length > 0 ? selectors : ([] as any);
  }

  if (formData.clickCssSelector !== undefined) {
    let trimmed = (formData.clickCssSelector as string)?.trim();
    source.clickCssSelector = trimmed || undefined;
  }

  if (formData.waitForSelector !== undefined) {
    let trimmed = (formData.waitForSelector as string)?.trim();
    source.waitForSelector = trimmed || undefined;
  }
}

function updateCrawlerSettings(
  source: ContentImportSource,
  formData: Record<string, unknown>,
): void {
  if (formData.useSitemap !== undefined) {
    source.useSitemap = formData.useSitemap as boolean;
  }

  source.setActorData({
    actor_name: ActorName.WebsiteContentCrawler,
  });
}

function updateAudienceSegments(
  source: ContentImportSource,
  formData: Record<string, unknown>,
): void {
  if (formData.selectedSegmentIds !== undefined) {
    let segmentIds = formData.selectedSegmentIds as number[];
    source.contentSegmentIds = segmentIds.length > 0 ? segmentIds : ([] as any);
  }

  if (formData.audienceRules !== undefined) {
    let rules = formData.audienceRules as ContentSegmentRule[];
    source.contentSegmentRules = rules.map((rule) => ({
      id: rule.id ?? null,
      name: rule.name,
      raw_predicates: rule.raw_predicates,
      ai_content_segment_ids: rule.ai_content_segment_ids,
      is_dirty: rule.is_dirty ?? true,
    }));
  }
}

function updateContentAccessSettings(
  source: ContentImportSource,
  formData: Record<string, unknown>,
): void {
  if (formData.finEnabled !== undefined) {
    source.chatbotAvailability = formData.finEnabled ? FinAvailability.ON : FinAvailability.OFF;
  }

  if (formData.copilotEnabled !== undefined) {
    source.copilotAvailability = formData.copilotEnabled ? FinAvailability.ON : FinAvailability.OFF;
  }
}

function parseMultilineUrls(text: string): string[] {
  return text
    .split('\n')
    .map((url) => url.trim())
    .filter((url) => url);
}

function parseCommaSeparated(text: string): string[] {
  return text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item);
}
